import React, { useState, useMemo } from 'react';
import { Search, Code, Copy, Check, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { CODE_SNIPPETS, searchSnippets } from '../data/codeSnippets';
import CodeBlock from './CodeBlock';

const CATEGORY_ICONS = {
  javascript: '🟨',
  python: '🐍',
  react: '⚛️',
  html: '🌐',
  css: '🎨',
  sql: '🗄️',
  bash: '💻',
  api: '🔌'
};

const SnippetBrowserModal = ({ open, onClose, onSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState('javascript');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewSnippet, setPreviewSnippet] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const categories = Object.entries(CODE_SNIPPETS).map(([key, val]) => ({
    key,
    label: val.label,
    icon: CATEGORY_ICONS[key] || '📄',
    count: val.snippets.length
  }));

  const filteredSnippets = useMemo(() => {
    if (searchQuery.trim()) {
      return searchSnippets(searchQuery);
    }
    return CODE_SNIPPETS[selectedCategory]?.snippets || [];
  }, [selectedCategory, searchQuery]);

  const handleSelectSnippet = (snippet) => {
    onSelect({
      code: snippet.code,
      language: snippet.category === 'react' ? 'typescript' : 
                snippet.category === 'api' ? 'javascript' :
                snippet.category === 'bash' ? 'bash' :
                snippet.category
    });
    onClose();
  };

  const handleCopy = async (snippet) => {
    await navigator.clipboard.writeText(snippet.code);
    setCopiedId(snippet.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Code className="text-indigo-600" size={24} />
            Code Snippet Library
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search snippets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Category Sidebar */}
          {!searchQuery && (
            <div className="w-48 border-r border-slate-200 bg-slate-50 p-2">
              <ScrollArea className="h-full">
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={`w-full text-left px-3 py-2 rounded-lg mb-1 flex items-center gap-2 transition-colors ${
                      selectedCategory === cat.key 
                        ? 'bg-indigo-100 text-indigo-700' 
                        : 'hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span className="flex-1 text-sm font-medium">{cat.label}</span>
                    <span className="text-xs text-slate-400">{cat.count}</span>
                  </button>
                ))}
              </ScrollArea>
            </div>
          )}

          {/* Snippets List */}
          <div className={`${previewSnippet ? 'w-1/3' : 'flex-1'} border-r border-slate-200 overflow-hidden`}>
            <ScrollArea className="h-full">
              <div className="p-4 space-y-2">
                {searchQuery && (
                  <p className="text-sm text-slate-500 mb-3">
                    {filteredSnippets.length} result{filteredSnippets.length !== 1 ? 's' : ''} for "{searchQuery}"
                  </p>
                )}
                
                {filteredSnippets.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Code size={48} className="mx-auto mb-4 opacity-30" />
                    <p>No snippets found</p>
                  </div>
                ) : (
                  filteredSnippets.map(snippet => (
                    <div
                      key={snippet.id}
                      onClick={() => setPreviewSnippet(snippet)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        previewSnippet?.id === snippet.id
                          ? 'border-indigo-300 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">
                            {snippet.name}
                          </h4>
                          <p className="text-sm text-slate-500 line-clamp-2">
                            {snippet.description}
                          </p>
                          {searchQuery && snippet.categoryLabel && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-slate-200 rounded">
                              {snippet.categoryLabel}
                            </span>
                          )}
                        </div>
                        <ChevronRight size={18} className="text-slate-400 flex-shrink-0 mt-1" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Preview Panel */}
          {previewSnippet && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{previewSnippet.name}</h3>
                  <p className="text-sm text-slate-500">{previewSnippet.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(previewSnippet)}
                  >
                    {copiedId === previewSnippet.id ? (
                      <>
                        <Check size={14} className="mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={14} className="mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSelectSnippet(previewSnippet)}
                  >
                    Use Snippet
                  </Button>
                </div>
              </div>
              
              <ScrollArea className="flex-1">
                <div className="p-4">
                  <CodeBlock
                    code={previewSnippet.code}
                    language={
                      previewSnippet.category === 'react' ? 'typescript' :
                      previewSnippet.category === 'api' ? 'javascript' :
                      previewSnippet.category === 'bash' ? 'bash' :
                      previewSnippet.category || 'javascript'
                    }
                    theme="github-dark"
                    showLineNumbers={true}
                  />
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SnippetBrowserModal;
